/**
 * Meeting notes input component.
 */
import { useRef, useState } from 'react'
import { apiClient } from '@/services/api'

function useVoiceInput(onResult: (text: string) => void) {
  const [isListening, setIsListening] = useState(false)
  const [supported] = useState(
    () => !!(window as any).SpeechRecognition || !!(window as any).webkitSpeechRecognition
  )
  const recognitionRef = useRef<any>(null)

  const toggle = () => {
    if (isListening) {
      recognitionRef.current?.stop()
      return
    }
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    const recognition = new SR()
    recognition.continuous = true
    recognition.interimResults = false
    recognition.lang = 'en-US'
    recognition.onresult = (e: any) => {
      const transcript = Array.from(e.results)
        .map((r: any) => r[0].transcript)
        .join(' ')
      onResult(transcript)
    }
    recognition.onend = () => setIsListening(false)
    recognition.start()
    recognitionRef.current = recognition
    setIsListening(true)
  }

  return { isListening, supported, toggle }
}

interface NotesInputProps {
  value: string
  onChange: (value: string) => void
  disabled?: boolean
}

export const NotesInput: React.FC<NotesInputProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const cameraInputRef = useRef<HTMLInputElement>(null)
  const [isTranscribing, setIsTranscribing] = useState(false)
  const [transcribeError, setTranscribeError] = useState<string | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const voice = useVoiceInput((text) => onChange(value ? `${value} ${text}` : text))

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setTranscribeError(null)
    setImagePreview(URL.createObjectURL(file))
    setIsTranscribing(true)

    try {
      const result = await apiClient.transcribeImage(file)
      onChange(value ? `${value}\n${result.transcribed_text}` : result.transcribed_text)
    } catch (err: any) {
      setTranscribeError(err.message || 'Failed to transcribe image')
      setImagePreview(null)
    } finally {
      setIsTranscribing(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
      if (cameraInputRef.current) cameraInputRef.current.value = ''
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label htmlFor="notes" className="label mb-0">
          Meeting Notes
        </label>
        <div className="flex items-center gap-2">
          {voice.supported && (
            <button
              type="button"
              onClick={voice.toggle}
              disabled={disabled || isTranscribing}
              title={voice.isListening ? 'Stop recording' : 'Dictate notes'}
              className={`btn-secondary text-sm py-1.5 px-3 flex items-center gap-1.5 ${voice.isListening ? 'bg-red-100 border-red-300 text-red-700' : ''}`}
            >
              <svg className={`h-4 w-4 ${voice.isListening ? 'animate-pulse' : ''}`} fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
                <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
              </svg>
              {voice.isListening ? 'Stop' : 'Voice'}
            </button>
          )}
          <button
            type="button"
            onClick={() => cameraInputRef.current?.click()}
            disabled={disabled || isTranscribing || voice.isListening}
            className="btn-secondary text-sm py-1.5 px-3 flex items-center gap-1.5"
          >
            {isTranscribing ? (
              <>
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Transcribing...
              </>
            ) : (
              <>
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Camera
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || isTranscribing || voice.isListening}
            className="btn-secondary text-sm py-1.5 px-3 flex items-center gap-1.5"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="sm:hidden">Upload Notes</span>
            <span className="hidden sm:inline">Upload Photo</span>
          </button>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        className="hidden"
        onChange={handleImageUpload}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleImageUpload}
      />

      {imagePreview && !isTranscribing && (
        <div className="mb-2 flex items-center gap-2">
          <img src={imagePreview} alt="Uploaded notes" className="h-12 w-12 object-cover rounded border border-gray-200" />
          <span className="text-xs text-gray-500">Image transcribed successfully</span>
          <button
            type="button"
            onClick={() => setImagePreview(null)}
            className="text-xs text-red-500 hover:text-red-700"
          >
            Clear
          </button>
        </div>
      )}

      {transcribeError && (
        <p className="text-sm text-red-600 mb-2">{transcribeError}</p>
      )}

      <textarea
        id="notes"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="textarea min-h-[400px] font-mono text-sm"
        placeholder="Paste your meeting notes here, or upload an image of handwritten notes above...

Example:
- Discussed Q1 marketing strategy
- Team agreed on new brand direction
- Sarah to lead social media campaign
- Budget approved: $50K
- Launch date: March 15
- Next meeting: Feb 1"
        disabled={disabled || isTranscribing}
        maxLength={10000}
      />
      <div className="flex justify-between items-center mt-1">
        <p className="text-sm text-gray-600">
          {value.length} / 10,000 characters
        </p>
        {value.length === 0 && !isTranscribing && (
          <p className="text-sm text-amber-600">
            💡 Tip: The more detailed your notes, the better the report
          </p>
        )}
      </div>
    </div>
  )
}
